import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Sparkles, Activity, Plus, Trash2 } from "lucide-react";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { errMsg } from "../lib/errors";

const iCl = "w-full bg-[var(--pa-bg-2)] border border-[var(--pa-border)] focus:border-[var(--pa-gold)] focus:outline-none rounded text-sm text-[var(--pa-text)] p-2";

function emptyTooth(n) {
  return { tooth: n, pd_mb: 3, pd_b: 3, pd_db: 3, pd_dl: 3, pd_l: 3, pd_ml: 3, bop_sites: 0, recession_mm: 0, bone_loss_pct: 0, bone_loss_pattern: "horizontal", furcation: null, mobility: null };
}

export default function PerioProstho() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [busy, setBusy] = useState(false);
  const [patientId, setPatientId] = useState(sp.get("patient_id") || "");
  const [smokerPY, setSmokerPY] = useState("");
  const [hba1c, setHba1c] = useState("");
  const [meds, setMeds] = useState("");
  const [planned, setPlanned] = useState("");
  const [notes, setNotes] = useState("");
  // Default — adult dentition with 4 representative quadrant teeth; clinician adds more
  const [teeth, setTeeth] = useState([3, 14, 19, 30].map(emptyTooth));

  const updTooth = (idx, field, val) => setTeeth((t) => t.map((x, i) => i === idx ? { ...x, [field]: val } : x));
  const addTooth = () => {
    const used = new Set(teeth.map((t) => t.tooth));
    const next = [...Array(32).keys()].map((n) => n + 1).find((n) => !used.has(n));
    if (next) setTeeth((t) => [...t, emptyTooth(next)]);
  };
  const removeTooth = (idx) => setTeeth((t) => t.filter((_, i) => i !== idx));

  const submit = async () => {
    if (teeth.length === 0) { toast.error("Add at least one tooth"); return; }
    setBusy(true);
    try {
      const payload = {
        patient_id: patientId || null,
        teeth: teeth.map((t) => ({
          ...t,
          pd_mb: parseInt(t.pd_mb) || 0, pd_b: parseInt(t.pd_b) || 0, pd_db: parseInt(t.pd_db) || 0,
          pd_dl: parseInt(t.pd_dl) || 0, pd_l: parseInt(t.pd_l) || 0, pd_ml: parseInt(t.pd_ml) || 0,
          bop_sites: parseInt(t.bop_sites) || 0,
          recession_mm: parseInt(t.recession_mm) || 0,
          bone_loss_pct: t.bone_loss_pct === "" ? null : parseInt(t.bone_loss_pct),
        })),
        smoker_pack_years: smokerPY ? parseFloat(smokerPY) : null,
        diabetes_hba1c: hba1c ? parseFloat(hba1c) : null,
        medications_affecting_healing: meds ? meds.split(",").map((s) => s.trim()).filter(Boolean) : [],
        planned_prostho: planned ? planned.split(",").map((s) => s.trim()).filter(Boolean) : [],
        clinician_notes: notes || null,
      };
      const r = await api.post("/perio-prostho/analyze", payload);
      toast.success("Perio analysis complete");
      nav(`/perio-prostho/${r.data.id}`);
    } catch (e) {
      toast.error(errMsg(e, "Perio analysis failed"));
    } finally { setBusy(false); }
  };

  return (
    <Layout>
      <div className="kicker">Phase 1 · Diagnostics</div>
      <h1 data-testid="perio-title" className="font-serif text-3xl text-[var(--pa-text)] mt-1 flex items-center gap-3">
        <Activity size={22} className="text-[var(--pa-gold)]" /> Perio-Prostho Planner
      </h1>
      <p className="mt-2 text-sm text-[var(--pa-text-muted)] max-w-2xl">
        AAP/EFP 2018 staging + grading and Kwok &amp; Caton tooth prognosis — sequenced to
        clear periodontal disease BEFORE definitive prosthodontics.
      </p>

      <div className="mt-7 grid md:grid-cols-2 gap-4">
        <Field label="Patient ID (optional)">
          <input value={patientId} onChange={(e) => setPatientId(e.target.value)} data-testid="perio-patient-id" className={iCl} />
        </Field>
        <Field label="Smoker (pack-years)">
          <input type="number" step="0.1" value={smokerPY} onChange={(e) => setSmokerPY(e.target.value)} className={iCl} />
        </Field>
        <Field label="Diabetes HbA1c">
          <input type="number" step="0.1" value={hba1c} onChange={(e) => setHba1c(e.target.value)} className={iCl} />
        </Field>
        <Field label="Meds affecting healing (comma-separated)">
          <input value={meds} onChange={(e) => setMeds(e.target.value)} className={iCl} placeholder="bisphosphonates, anticoagulants" />
        </Field>
        <Field label="Planned prostho (comma-separated)">
          <input value={planned} onChange={(e) => setPlanned(e.target.value)} className={iCl} placeholder="implant_#19, PFM_#3" data-testid="perio-planned" />
        </Field>
      </div>

      <section className="mt-7">
        <div className="flex items-center justify-between mb-3">
          <div className="kicker">Probing Chart (per tooth, mm)</div>
          <button type="button" onClick={addTooth} data-testid="perio-add-tooth"
            className="flex items-center gap-1 text-xs text-[var(--pa-gold)] hover:underline">
            <Plus size={12} /> Add tooth
          </button>
        </div>

        <div className="space-y-2" data-testid="perio-teeth">
          {teeth.map((t, idx) => (
            <div key={idx} className="bg-[var(--pa-bg-2)] border border-[var(--pa-border)] rounded p-3">
              <div className="flex items-center gap-2 mb-2">
                <label className="text-xs text-[var(--pa-text-dim)]">Tooth #</label>
                <input type="number" min={1} max={32} value={t.tooth} onChange={(e) => updTooth(idx, "tooth", parseInt(e.target.value) || 1)}
                  className="w-16 bg-[var(--pa-bg)] border border-[var(--pa-border)] rounded px-2 py-1 text-sm text-[var(--pa-text)]" />
                <button type="button" onClick={() => removeTooth(idx)} className="ml-auto text-red-400 hover:text-red-300"><Trash2 size={14} /></button>
              </div>
              <div className="grid grid-cols-6 gap-1.5 text-xs">
                {["pd_mb", "pd_b", "pd_db", "pd_dl", "pd_l", "pd_ml"].map((k) => (
                  <div key={k}>
                    <div className="text-[10px] text-[var(--pa-text-dim)] uppercase">{k.replace("pd_", "")}</div>
                    <input type="number" min={0} max={15} value={t[k]} onChange={(e) => updTooth(idx, k, e.target.value)}
                      className="w-full bg-[var(--pa-bg)] border border-[var(--pa-border)] rounded px-1.5 py-1 text-center text-[var(--pa-text)]" />
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-1.5 mt-2 text-xs">
                <FieldMini label="BoP /6"><input type="number" min={0} max={6} value={t.bop_sites} onChange={(e) => updTooth(idx, "bop_sites", e.target.value)} className="w-full bg-[var(--pa-bg)] border border-[var(--pa-border)] rounded px-1.5 py-1 text-[var(--pa-text)]" /></FieldMini>
                <FieldMini label="Recession"><input type="number" min={0} max={15} value={t.recession_mm} onChange={(e) => updTooth(idx, "recession_mm", e.target.value)} className="w-full bg-[var(--pa-bg)] border border-[var(--pa-border)] rounded px-1.5 py-1 text-[var(--pa-text)]" /></FieldMini>
                <FieldMini label="Bone loss %"><input type="number" min={0} max={100} value={t.bone_loss_pct ?? ""} onChange={(e) => updTooth(idx, "bone_loss_pct", e.target.value)} className="w-full bg-[var(--pa-bg)] border border-[var(--pa-border)] rounded px-1.5 py-1 text-[var(--pa-text)]" /></FieldMini>
                <FieldMini label="Furcation">
                  <select value={t.furcation || ""} onChange={(e) => updTooth(idx, "furcation", e.target.value || null)} className="w-full bg-[var(--pa-bg)] border border-[var(--pa-border)] rounded px-1 py-1 text-[var(--pa-text)]">
                    <option value="">—</option><option>I</option><option>II</option><option>III</option>
                  </select>
                </FieldMini>
                <FieldMini label="Mobility">
                  <select value={t.mobility || ""} onChange={(e) => updTooth(idx, "mobility", e.target.value || null)} className="w-full bg-[var(--pa-bg)] border border-[var(--pa-border)] rounded px-1 py-1 text-[var(--pa-text)]">
                    <option value="">—</option><option>I</option><option>II</option><option>III</option>
                  </select>
                </FieldMini>
              </div>
            </div>
          ))}
        </div>
      </section>

      <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Clinician notes"
        className={`${iCl} mt-6 w-full`} data-testid="perio-notes" />

      <button onClick={submit} disabled={busy} data-testid="perio-submit"
        className="mt-6 flex items-center gap-2 bg-[var(--pa-gold)] text-[var(--pa-bg)] font-semibold px-6 py-3 rounded-md hover:bg-[var(--pa-gold-hover)] transition disabled:opacity-60">
        {busy ? <><Loader2 size={14} className="animate-spin" /> Analyzing…</> : <><Sparkles size={14} /> Run Perio analysis</>}
      </button>
    </Layout>
  );
}

function Field({ label, children }) {
  return <div><label className="kicker block mb-1">{label}</label>{children}</div>;
}
function FieldMini({ label, children }) {
  return <div><div className="text-[10px] text-[var(--pa-text-dim)] uppercase">{label}</div>{children}</div>;
}
